import React from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { GqlImageBanner, ImageBannerType } from 'types/graphql';
import RouteLeavingGuard from 'src/routes/route-leaving-guard';
import KeepEditingModal from 'src/dispensary-admin/keep-editing-modal';
import { ExpandableStep, getImageBannerSteps, ImageBannerStepsEnums, ImageBannerStepsStatus } from './expandable-step';
import { TypeStep } from './type-step';
import { ConnectionStep } from './connection-step';
import { ConfigurationStep } from './configuration-step/configuration-step';
import { ScheduleStep } from './schedule-step/schedule-step';
import { ConfirmStep } from './confirm-step/confirm-step';
import { useAddEditImageBanner } from './use-add-edit-image-banner';
import { Container } from './add-edit-image-banner.styles';

type AddEditImageBannerProps = {
  bannerToEdit: GqlImageBanner | null;
  onSave: (banner: GqlImageBanner) => Promise<void>;
  onClose: () => void;
};

export function AddEditImageBanner({ bannerToEdit, onSave, onClose }: AddEditImageBannerProps): JSX.Element {
  const flags = useFlags();
  const isConnectedBannersEnabled = flags['growth.ecomm.connected-banners.rollout'] ?? false;

  const {
    banner,
    setBannerData,
    currentStep,
    setStepNumber,
    setEditStatus,
    isDirty,
    setIsDirty,
    handleFile,
    isUploadingImage,
    isUploadingMobileImage,
    handleSave,
    isSaving,
  } = useAddEditImageBanner({ bannerToEdit, onSave, onClose, isConnectedBannersEnabled });

  // Only block navigation when there are unsaved changes AND we're not currently saving
  const shouldBlockNavigation = isDirty && !isSaving;

  // Get the banner type, defaulting to 'link' for backwards compatibility
  const bannerType = banner?.bannerType ?? ImageBannerType.link;

  // Get steps based on feature flags and banner type
  const steps = getImageBannerSteps(bannerType, isConnectedBannersEnabled);

  return (
    <Container>
      {steps.map(({ title, key, description }, index: number) => {
        const indexToStep = index + 1;

        return (
          <ExpandableStep
            title={title}
            key={key}
            description={description}
            expanded={indexToStep === currentStep}
            isEditDisabled={!setEditStatus(key)}
            onEditClick={() => setStepNumber(indexToStep)}
            status={indexToStep === currentStep ? ImageBannerStepsStatus.current : ImageBannerStepsStatus.incomplete}
            stepNumber={indexToStep}
            isReadOnly={false}
            hasPassedStep={setEditStatus(key)}
          >
            <>
              {key === ImageBannerStepsEnums.type && (
                <TypeStep
                  banner={banner}
                  currentStepIndex={index}
                  setStepNumber={setStepNumber}
                  setBannerData={setBannerData}
                  setIsDirty={setIsDirty}
                />
              )}

              {key === ImageBannerStepsEnums.connection && (
                <ConnectionStep
                  banner={banner}
                  currentStepIndex={index}
                  setStepNumber={setStepNumber}
                  setBannerData={setBannerData}
                  setIsDirty={setIsDirty}
                />
              )}

              {key === ImageBannerStepsEnums.configuration && (
                <ConfigurationStep
                  banner={banner}
                  currentStepIndex={index}
                  setStepNumber={setStepNumber}
                  setBannerData={setBannerData}
                  setIsDirty={setIsDirty}
                  handleFile={handleFile}
                  isUploadingImage={isUploadingImage}
                  isUploadingMobileImage={isUploadingMobileImage}
                />
              )}

              {key === ImageBannerStepsEnums.schedule && (
                <ScheduleStep
                  banner={banner}
                  currentStepIndex={index}
                  setStepNumber={setStepNumber}
                  setBannerData={setBannerData}
                  setIsDirty={setIsDirty}
                />
              )}

              {key === ImageBannerStepsEnums.confirm && (
                <ConfirmStep
                  banner={banner}
                  setStepNumber={setStepNumber}
                  handleSave={handleSave}
                  isSaving={isSaving}
                />
              )}

              <RouteLeavingGuard when={shouldBlockNavigation} modal={KeepEditingModal} />
            </>
          </ExpandableStep>
        );
      })}
    </Container>
  );
}
